/**
 * Per-species steering and combat logic (PRD §16, §17).
 *
 * `entities.ts` owns the pools, the damage resolution and the shared rules that
 * every enemy obeys; this file only decides where a given creature wants to be
 * and when it shoots. Each behaviour is a plain function over one enemy, which
 * keeps the roster readable — the whole design of an enemy is one screenful.
 *
 * The imports from `entities` are type-only, so the two modules do not form a
 * runtime cycle.
 */

import { angleDelta, clamp, damp, dampAngle, hyp } from '../core/math';
import { density, featuresFor, gradient } from './field';
import { laneZ, toField } from './mesher';
import {
  DREAD, DRONE, HUNTER, LURKER, MINE, SEEKER, SENTINEL, SWARMER, TURRET,
  type EnemyType,
} from './config';
import type { Actors, ActorCtx, Enemy } from './entities';

/** Types that a sonar ping wakes up and that read as machines to the HUD. */
export const MECHANICAL: ReadonlySet<EnemyType> = new Set<EnemyType>([
  'drone', 'seeker', 'turret', 'hunter', 'sentinel', 'dreadnought', 'mine',
]);

const n = { x: 0, y: 0 };
/** Scratch for lane-aware field lookups. */
const scratch = { x: 0, y: 0 };

// ---------------------------------------------------------------------------
// shared helpers
// ---------------------------------------------------------------------------

/**
 * Push away from rock the enemy is about to swim into. Looks ahead along the
 * current velocity rather than at the current position, so a fast mover turns
 * before it is already embedded.
 */
function avoid(e: Enemy, dt: number, look: number, push: number) {
  const ax = e.x + e.vx * look;
  const ay = e.y + e.vy * look;
  // Sampled in the lane the creature swims in. The look-ahead point is a DRAWN
  // position; the rock it is asking about lives at the lane's field offset, and
  // on lane 0 the two are the same point so nothing changes there.
  const f = toField(Math.max(0, e.lane), ax, ay, scratch);
  const feats = featuresFor(f.x - 8, f.x + 8);
  if (density(f.x, f.y, feats) <= -6) return;
  gradient(f.x, f.y, n, 0.55, feats);
  // The normal comes back in field space, but the lane transform is a shift in
  // x and a shear in y, so the direction carries over unchanged and the steer
  // is applied to the drawn velocity as before.
  e.vx += n.x * push * dt;
  e.vy += n.y * push * dt;
}

/** Steer toward a goal, cap the speed, apply drag, integrate. */
function seek(e: Enemy, gx: number, gy: number, accel: number, max: number, dt: number, drag = 1.5) {
  const dx = gx - e.x, dy = gy - e.y;
  const d = hyp(dx, dy) || 1;
  e.vx += (dx / d) * accel * dt;
  e.vy += (dy / d) * accel * dt;
  const sp = hyp(e.vx, e.vy);
  if (sp > max) { e.vx = (e.vx / sp) * max; e.vy = (e.vy / sp) * max; }
  e.vx -= e.vx * drag * dt;
  e.vy -= e.vy * drag * dt;
  e.x += e.vx * dt;
  e.y += e.vy * dt;
  if (Math.abs(e.vx) > 0.4) e.facing = Math.sign(e.vx);
}

/** Can this enemy currently perceive the player? Shared by every hunter. */
function perceive(
  e: Enemy, dt: number, dist: number, ctx: ActorCtx,
  lit: number, dark: number, hear: number = DRONE.hearRange,
): boolean {
  // A marcher has already found you — see `Enemy.march`. Held open here rather
  // than by widening the ranges, so nothing about the campaign's numbers moves.
  if (e.march) {
    e.lastSeenX = ctx.px;
    e.lastSeenY = ctx.py;
    e.alert = 1;
    return ctx.playerAlive;
  }
  let range = ctx.lightOn ? lit : dark;
  if (ctx.noisy) range = Math.max(range, hear);
  const canSee = dist < range && ctx.playerAlive && ctx.hasLineOfSight(e.x, e.y);
  if (canSee) {
    e.alert = Math.min(1, e.alert + dt * (2.2 - dist / range));
    e.lastSeenX = ctx.px;
    e.lastSeenY = ctx.py;
  } else {
    e.alert = Math.max(0, e.alert - dt * 0.32);
  }
  return canSee;
}

function setState(e: Enemy, s: Enemy['state']) {
  if (e.state === s) return;
  e.state = s;
  e.stateTime = 0;
}

/** Lead a shot so a moving player still has to dodge. */
function leadAngle(e: Enemy, ctx: ActorCtx, speed: number, lead = 0.55, jitter = 0.09): number {
  const dist = hyp(ctx.px - e.x, ctx.py - e.y);
  const t = dist / speed;
  const aimX = ctx.px + ctx.pvx * t * lead;
  const aimY = ctx.py + ctx.pvy * t * lead;
  return Math.atan2(aimY - e.y, aimX - e.x) + (Math.random() - 0.5) * jitter;
}

// ---------------------------------------------------------------------------
// dispatch
// ---------------------------------------------------------------------------

export function update(
  a: Actors, e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx,
) {
  switch (e.type) {
    case 'mine': return mine(a, e, dt, dist, ctx);
    case 'seeker': return seeker(a, e, dt, dx, dy, dist, ctx);
    case 'jelly': return jelly(e, dt, dist, a.time);
    case 'bloom': return bloom(e, dt, dx, dy, dist, a.time);
    case 'swarmer': return swarmer(a, e, dt, dx, dy, dist, ctx);
    case 'lurker': return lurker(e, dt, dx, dy, dist, ctx);
    case 'turret': return turret(a, e, dt, dist, ctx);
    case 'hunter': return hunter(a, e, dt, dx, dy, dist, ctx);
    case 'sentinel': return sentinel(a, e, dt, dx, dy, dist, ctx);
    case 'dreadnought': return dreadnought(a, e, dt, dx, dy, dist, ctx);
    default: return drone(a, e, dt, dx, dy, dist, ctx);
  }
}

// ---------------------------------------------------------------------------
// mines
// ---------------------------------------------------------------------------

function mine(a: Actors, e: Enemy, dt: number, dist: number, ctx: ActorCtx) {
  // Slow drift on its mooring.
  e.phase += dt * 0.55;
  const tx = e.homeX + Math.sin(e.phase * 0.7) * 2.4;
  const ty = e.homeY + Math.sin(e.phase * 0.43 + 1.1) * 1.8;
  e.x = damp(e.x, tx, 1.4, dt);
  e.y = damp(e.y, ty, 1.4, dt);
  e.spin += dt * 0.25;

  if (dist < MINE.triggerRange && ctx.playerAlive) {
    e.cooldown += dt;
    if (e.cooldown > MINE.armTime * 0.25) a.kill(e, ctx);
  } else {
    e.cooldown = 0;
  }
  e.alert = clamp(1 - (dist - MINE.triggerRange) / 22, 0, 1);
}

/**
 * A mine that comes looking. It sleeps on its mooring until it hears the sub,
 * then closes at a speed you can outrun — the threat is that it follows you
 * into the next fight, not that it catches you in the open.
 */
function seeker(a: Actors, e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx) {
  e.spin += dt * (0.4 + e.alert * 5);

  const heard = e.march
    || (dist < SEEKER.wakeRange && (ctx.noisy || ctx.lightOn || dist < SEEKER.wakeRange * 0.5));
  if (heard && ctx.playerAlive) {
    e.alert = Math.min(1, e.alert + dt * 1.6);
    e.timer = 0;
    e.lastSeenX = ctx.px;
    e.lastSeenY = ctx.py;
  } else {
    e.timer += dt;
    if (e.timer > SEEKER.patience) e.alert = Math.max(0, e.alert - dt * 0.7);
  }
  e.charge = e.alert;

  if (e.alert > 0.4) {
    setState(e, 'attack');
    seek(e, e.lastSeenX, e.lastSeenY, SEEKER.accel, SEEKER.speed * e.alert, dt, 1.1);
    avoid(e, dt, 0.5, 40);
    if (dist < SEEKER.triggerRange && ctx.playerAlive) a.kill(e, ctx);
  } else {
    setState(e, 'idle');
    // Drifts home and re-moors.
    e.phase += dt * 0.5;
    seek(e, e.homeX + Math.sin(e.phase * 0.6) * 3, e.homeY + Math.sin(e.phase * 0.4) * 2, 6, 4, dt, 2);
  }
  void dx; void dy;
}

// ---------------------------------------------------------------------------
// drifting life
// ---------------------------------------------------------------------------

function jelly(e: Enemy, dt: number, dist: number, time: number) {
  // `phase` is a fixed per-animal offset; the clock is shared.
  const t = time * e.rate + e.phase;
  // A slow wandering drift; medusae climb on the contraction and sink between.
  const beat = Math.sin(t);
  e.vx = damp(e.vx, Math.sin(t * 0.19 + e.phase) * 2.4, 0.8, dt);
  e.vy = damp(e.vy, Math.max(0, beat) * 2.4 - 0.7, 1.4, dt);
  e.x += e.vx * dt;
  e.y += e.vy * dt;

  // Turn away from rock rather than swimming into it.
  const feats = featuresFor(e.x - 9, e.x + 9);
  if (density(e.x, e.y, feats) > -6) {
    gradient(e.x, e.y, n, 0.55, feats);
    e.x += n.x * 6 * dt;
    e.y += n.y * 6 * dt;
  }

  e.alert = dist < 26 ? 1 : 0;
}

/**
 * The bloom. Where a medusa drifts, this one jets: it accelerates on each
 * contraction and coasts between, so it crosses water in lurches and is much
 * harder to lead a shot on.
 */
function bloom(e: Enemy, dt: number, dx: number, dy: number, dist: number, time: number) {
  const t = time * e.rate + e.phase;
  const beat = Math.sin(t);
  // The contraction is the power stroke; the rest of the cycle is a coast.
  const stroke = Math.max(0, beat) ** 3;

  e.alert = damp(e.alert, dist < 74 ? 1 : 0, 1.4, dt);

  // Aims at the sub when roused, otherwise mills around where it lives.
  let ax: number, ay: number;
  if (e.alert > 0.4 && dist > 1) {
    ax = dx / dist;
    ay = dy / dist;
  } else {
    const wander = Math.sin(t * 0.31 + e.phase * 2.1);
    ax = Math.cos(wander * 2.4);
    ay = Math.sin(wander * 1.7) * 0.5;
  }

  e.vx += ax * 26 * stroke * dt;
  e.vy += ay * 26 * stroke * dt + 1.2 * dt;
  // Heavy drag between strokes is what makes it lurch rather than glide.
  e.vx -= e.vx * 1.5 * dt;
  e.vy -= e.vy * 1.7 * dt;

  const feats = featuresFor(e.x - 12, e.x + 12);
  if (density(e.x + e.vx * 0.5, e.y + e.vy * 0.5, feats) > -8) {
    gradient(e.x, e.y, n, 0.55, feats);
    e.vx += n.x * 26 * dt;
    e.vy += n.y * 26 * dt;
  }

  e.x += e.vx * dt;
  e.y += e.vy * dt;
  if (Math.abs(e.vx) > 0.5) e.facing = Math.sign(e.vx);
}

// ---------------------------------------------------------------------------
// shoal
// ---------------------------------------------------------------------------

/**
 * Swarmers are individually trivial and only interesting as a flock: they
 * cohere on their group, hold each other off, and take turns lunging so the
 * shoal pulses instead of merging into one blob on the player's nose.
 */
function swarmer(a: Actors, e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx) {
  const hunting = (e.march || dist < SWARMER.noticeRange) && ctx.playerAlive;
  if (hunting) {
    e.alert = 1;
    e.timer2 = 0;
    e.lastSeenX = ctx.px;
    e.lastSeenY = ctx.py;
  } else {
    e.timer2 += dt;
    if (e.timer2 > SWARMER.patience) e.alert = Math.max(0, e.alert - dt);
  }

  // Flock forces, over the handful of shoal-mates actually nearby.
  let cx = 0, cy = 0, cn = 0, sx = 0, sy = 0;
  const shoal = a.swarmGroups.get(e.group);
  if (shoal) for (const o of shoal) {
    if (o === e || !o.alive) continue;
    const ox = o.x - e.x, oy = o.y - e.y;
    const od = hyp(ox, oy);
    if (od > 26) continue;
    cx += o.x; cy += o.y; cn++;
    if (od < SWARMER.separateRange && od > 0.001) {
      sx -= (ox / od) * (1 - od / SWARMER.separateRange);
      sy -= (oy / od) * (1 - od / SWARMER.separateRange);
    }
  }

  let gx: number, gy: number;
  if (e.alert > 0.3) {
    setState(e, 'attack');
    // Take turns: each fish carries its own lunge clock, offset by its phase.
    e.timer -= dt;
    if (e.timer <= 0) {
      e.timer = SWARMER.lungeGap * (0.7 + Math.random() * 0.8);
      const a2 = Math.atan2(dy, dx) + (Math.random() - 0.5) * 0.5;
      e.vx += Math.cos(a2) * SWARMER.lungeSpeed;
      e.vy += Math.sin(a2) * SWARMER.lungeSpeed;
      e.charge = 1;
    }
    e.charge = Math.max(0, e.charge - dt * 2.4);
    // Circle the target rather than converging on a point.
    const orbit = e.phase * 2;
    gx = ctx.px + Math.cos(a.time * 1.1 + orbit) * 11;
    gy = ctx.py + Math.sin(a.time * 1.3 + orbit) * 8;
  } else {
    setState(e, 'idle');
    gx = e.homeX + Math.sin(a.time * 0.5 + e.phase) * 14;
    gy = e.homeY + Math.sin(a.time * 0.37 + e.phase * 1.7) * 7;
  }

  if (cn) {
    gx = gx * 0.72 + (cx / cn) * 0.28 * SWARMER.cohere;
    gy = gy * 0.72 + (cy / cn) * 0.28 * SWARMER.cohere;
  }
  e.vx += sx * SWARMER.separate * SWARMER.accel * dt * 0.35;
  e.vy += sy * SWARMER.separate * SWARMER.accel * dt * 0.35;

  seek(e, gx, gy, SWARMER.accel, SWARMER.speed * (e.alert > 0.3 ? 1 : 0.4), dt, 2.4);
  avoid(e, dt, 0.4, 46);

  // Heading, not a left/right flag. `seek` sets `facing` from the sign of vx,
  // which is fine for something that only ever travels along the x axis and
  // useless for a fish: anything climbing or diving ends up drawn sideways.
  // The nose follows the velocity through a damped turn, exactly as the sub's
  // does, and the body banks into it.
  const speed = hyp(e.vx, e.vy);
  if (speed > 0.5) {
    const want = Math.atan2(e.vy, e.vx);
    const turn = angleDelta(e.aim, want);
    // Bank proportional to how hard it is turning, damped so it settles.
    e.spin = damp(e.spin, clamp(turn * 2.4, -0.9, 0.9), 5, dt);
    e.aim = dampAngle(e.aim, want, 9.0 + speed * 0.18, dt);
  } else {
    e.spin = damp(e.spin, 0, 3, dt);
  }
  // Flip only once the heading is decisively past vertical, so a fish hovering
  // nose-up does not strobe between left and right.
  const c = Math.cos(e.aim);
  if (c > 0.18) e.facing = 1;
  else if (c < -0.18) e.facing = -1;
}

// ---------------------------------------------------------------------------
// ambusher
// ---------------------------------------------------------------------------

/**
 * The lurker sits in the dark showing nothing but a lure, and only becomes a
 * threat inside striking distance. Its whole design is the moment of surprise,
 * so it telegraphs hard for a beat before it commits — you can always dodge it
 * if you were watching.
 */
function lurker(e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx) {
  switch (e.state) {
    case 'idle': {
      // The lure brightens as the sub gets close, which is the only warning.
      e.charge = damp(e.charge, dist < LURKER.lureRange ? clamp(1 - dist / LURKER.lureRange, 0, 1) : 0, 2, dt);
      e.phase += dt * 0.6;
      seek(
        e,
        e.homeX + Math.sin(e.phase) * 2.5,
        e.homeY + Math.sin(e.phase * 0.7) * 1.5,
        7, 3.5, dt, 2.4,
      );
      if (dist < LURKER.strikeRange && ctx.playerAlive && ctx.hasLineOfSight(e.x, e.y)) {
        setState(e, 'burst');
        e.timer = 0.42;
        e.lastSeenX = ctx.px;
        e.lastSeenY = ctx.py;
      }
      break;
    }
    case 'burst': {
      // Wind-up: it hauls back, jaw opening, before the rush.
      e.charge = 1;
      e.timer -= dt;
      e.vx = damp(e.vx, -dx * 0.18, 4, dt);
      e.vy = damp(e.vy, -dy * 0.18, 4, dt);
      e.x += e.vx * dt;
      e.y += e.vy * dt;
      e.lastSeenX = ctx.px;
      e.lastSeenY = ctx.py;
      if (e.timer <= 0) {
        setState(e, 'rush');
        e.timer = LURKER.rushTime;
        e.timer2 = 0;
        const a = Math.atan2(ctx.py - e.y, ctx.px - e.x);
        e.vx = Math.cos(a) * LURKER.rushSpeed * 0.4;
        e.vy = Math.sin(a) * LURKER.rushSpeed * 0.4;
      }
      break;
    }
    case 'rush': {
      e.timer -= dt;
      const a = Math.atan2(e.lastSeenY - e.y, e.lastSeenX - e.x);
      e.vx += Math.cos(a) * LURKER.rushAccel * dt;
      e.vy += Math.sin(a) * LURKER.rushAccel * dt;
      const sp = hyp(e.vx, e.vy);
      if (sp > LURKER.rushSpeed) {
        e.vx = (e.vx / sp) * LURKER.rushSpeed;
        e.vy = (e.vy / sp) * LURKER.rushSpeed;
      }
      e.x += e.vx * dt;
      e.y += e.vy * dt;
      if (Math.abs(e.vx) > 0.4) e.facing = Math.sign(e.vx);

      // One bite per rush, however long the two stay overlapped.
      if (e.timer2 === 0 && dist < ctx.playerRadius + e.radius + 1.5) {
        e.timer2 = 1;
        ctx.hitPlayer(LURKER.bite);
        ctx.onEnemyShot(e.x, e.y, 'bite');
        ctx.onSpark(e.x, e.y, dx, dy, 18);
        ctx.onShake(0.3);
      }
      if (e.timer <= 0) {
        setState(e, 'recover');
        e.timer = LURKER.recover;
      }
      break;
    }
    case 'recover':
    default: {
      // Winded and slow — this is the window to shoot it.
      e.charge = damp(e.charge, 0.2, 2, dt);
      e.timer -= dt;
      seek(e, e.homeX, e.homeY, 9, 7, dt, 2.2);
      if (e.timer <= 0) setState(e, 'idle');
      break;
    }
  }
  avoid(e, dt, 0.35, 34);
  e.alert = e.charge;
}

// ---------------------------------------------------------------------------
// emplacements
// ---------------------------------------------------------------------------

/**
 * A turret cannot chase, so it is allowed to hit hard and cover a lot of water.
 * The counterplay is entirely positional: get out of its arc, or put rock in
 * the way.
 */
function turret(a: Actors, e: Enemy, dt: number, dist: number, ctx: ActorCtx) {
  const mount = Math.atan2(e.mountY, e.mountX);
  const toPlayer = Math.atan2(ctx.py - e.y, ctx.px - e.x);
  const offMount = Math.abs(angleDelta(mount, toPlayer));
  const inArc = offMount < TURRET.arc;
  const canSee = dist < TURRET.range && inArc && ctx.playerAlive && ctx.hasLineOfSight(e.x, e.y);

  e.alert = canSee
    ? Math.min(1, e.alert + dt * 2.4)
    : Math.max(0, e.alert - dt * 0.6);

  // Traverse toward the lead point, or return to rest along the mount normal.
  const want = canSee ? leadAngle(e, ctx, TURRET.bulletSpeed, 0.7, 0) : mount;
  const d = angleDelta(e.aim, want);
  e.aim += clamp(d, -TURRET.traverse * dt, TURRET.traverse * dt);

  e.cooldown -= dt;
  if (!canSee) {
    if (e.timer > 0) e.timer = 0;
    return;
  }
  setState(e, e.timer > 0 ? 'burst' : 'attack');

  // Only fire once the barrel has actually caught up.
  if (Math.abs(angleDelta(e.aim, want)) > 0.14 && e.timer <= 0) return;

  if (e.cooldown <= 0) {
    if (e.timer <= 0) e.timer = TURRET.burst;
    e.timer--;
    e.cooldown = e.timer > 0 ? TURRET.burstGap : TURRET.reload;
    const muzzle = e.radius + 1.4;
    a.fireBullet(
      e.x + Math.cos(e.aim) * muzzle, e.y + Math.sin(e.aim) * muzzle,
      e.aim + (Math.random() - 0.5) * 0.05, 'enemy',
      TURRET.bulletSpeed, TURRET.bulletDamage,
    );
    ctx.onEnemyShot(e.x, e.y, 'turret');
    if (e.timer <= 0) e.timer = 0;
  }
}

// ---------------------------------------------------------------------------
// drone
// ---------------------------------------------------------------------------

function drone(a: Actors, e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx) {
  const canSee = perceive(e, dt, dist, ctx, DRONE.sightRange, DRONE.darkRange);

  switch (e.state) {
    case 'idle':
      if (e.alert > 0.25) setState(e, 'suspicious');
      break;
    case 'suspicious':
      if (e.alert > 0.7) setState(e, 'investigate');
      else if (e.alert < 0.08) setState(e, 'idle');
      break;
    case 'investigate':
      if (canSee && dist < DRONE.fireRange) setState(e, 'attack');
      else if (e.alert < 0.2) setState(e, 'search');
      break;
    case 'attack':
      if (!canSee || dist > DRONE.fireRange * 1.35) setState(e, 'search');
      break;
    case 'search':
      if (canSee) setState(e, 'investigate');
      else if (e.stateTime > 6.5) setState(e, 'idle');
      break;
    default:
      setState(e, 'idle');
      break;
  }

  let goalX = e.homeX, goalY = e.homeY;
  let speedMul = 0.34;
  switch (e.state) {
    case 'idle':
      goalX = e.homeX + Math.sin(e.phase + e.stateTime * 0.35) * 16;
      goalY = e.homeY + Math.sin(e.phase * 1.7 + e.stateTime * 0.27) * 6;
      break;
    case 'suspicious':
      goalX = e.homeX + (e.lastSeenX - e.homeX) * 0.25;
      goalY = e.homeY + (e.lastSeenY - e.homeY) * 0.25;
      speedMul = 0.5;
      break;
    case 'investigate':
      goalX = e.lastSeenX;
      goalY = e.lastSeenY;
      speedMul = 1;
      break;
    case 'attack': {
      // Stand off and strafe rather than ramming — the player needs room (PRD §12).
      const stand = 34;
      const side = Math.sin(e.stateTime * 1.1 + e.phase) * 16;
      const ang = Math.atan2(dy, dx);
      goalX = ctx.px - Math.cos(ang) * stand;
      goalY = ctx.py - Math.sin(ang) * stand + side;
      speedMul = 0.85;
      break;
    }
    case 'search':
      goalX = e.lastSeenX + Math.sin(e.stateTime * 1.3) * 26;
      goalY = e.lastSeenY + Math.cos(e.stateTime * 0.9) * 12;
      speedMul = 0.65;
      break;
  }

  seek(e, goalX, goalY, DRONE.accel, DRONE.speed * speedMul, dt);
  avoid(e, dt, 0.55, 42);
  e.spin += dt * (6 + hyp(e.vx, e.vy) * 1.4);
  e.phase += dt;

  e.cooldown -= dt;
  if (e.state === 'attack' && e.cooldown <= 0 && dist < DRONE.fireRange) {
    e.cooldown = DRONE.fireInterval * (0.8 + Math.random() * 0.5);
    const a2 = leadAngle(e, ctx, DRONE.bulletSpeed);
    a.fireBullet(
      e.x + Math.cos(a2) * 2.4, e.y + Math.sin(a2) * 2.4,
      a2, 'enemy', DRONE.bulletSpeed, DRONE.bulletDamage,
      e.lane, laneZ(e.lane),
    );
    ctx.onEnemyShot(e.x, e.y, 'gun');
  }
}

// ---------------------------------------------------------------------------
// sentinel
// ---------------------------------------------------------------------------

/**
 * Heavy, slow, and armoured across a wide frontal arc. The plate tracks the
 * player, but slower than the player can move — so the fight is about circling
 * rather than out-damaging it. Shots into the plate barely scratch it.
 */
export function sentinelSoak(e: Enemy, hx: number, hy: number): number {
  const inbound = Math.atan2(hy - e.y, hx - e.x);
  return Math.abs(angleDelta(e.aim, inbound)) < SENTINEL.plateArc ? SENTINEL.plateSoak : 1;
}

function sentinel(a: Actors, e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx) {
  const canSee = perceive(e, dt, dist, ctx, SENTINEL.sightRange, SENTINEL.sightRange * 0.5, 70);

  // The plate always tries to face the threat, and always lags behind it.
  const toPlayer = Math.atan2(dy, dx);
  const d = angleDelta(e.aim, toPlayer);
  e.aim += clamp(d, -SENTINEL.plateTrack * dt, SENTINEL.plateTrack * dt);
  e.charge = clamp(1 - Math.abs(angleDelta(e.aim, toPlayer)) / SENTINEL.plateArc, 0, 1);

  if (e.alert > 0.3 || canSee) {
    setState(e, 'attack');
    const stand = 46;
    const ang = toPlayer;
    seek(
      e,
      e.lastSeenX - Math.cos(ang) * stand,
      e.lastSeenY - Math.sin(ang) * stand + Math.sin(e.stateTime * 0.7) * 14,
      SENTINEL.accel, SENTINEL.speed, dt,
    );
    e.cooldown -= dt;
    if (canSee && dist < SENTINEL.fireRange && e.cooldown <= 0) {
      e.cooldown = SENTINEL.fireInterval * (0.85 + Math.random() * 0.35);
      const a2 = leadAngle(e, ctx, SENTINEL.bulletSpeed, 0.6, 0.05);
      a.fireBullet(
        e.x + Math.cos(a2) * 3.4, e.y + Math.sin(a2) * 3.4,
        a2, 'enemy', SENTINEL.bulletSpeed, SENTINEL.bulletDamage,
        e.lane, laneZ(e.lane),
      );
      ctx.onEnemyShot(e.x, e.y, 'gun');
    }
  } else {
    setState(e, 'idle');
    seek(
      e,
      e.homeX + Math.sin(e.stateTime * 0.3 + e.phase) * 18,
      e.homeY + Math.sin(e.stateTime * 0.21) * 7,
      SENTINEL.accel * 0.6, SENTINEL.speed * 0.35, dt,
    );
  }
  avoid(e, dt, 0.6, 46);
  e.spin += dt * 2;
}

// ---------------------------------------------------------------------------
// hunter submarine
// ---------------------------------------------------------------------------

/**
 * The enemy submarine is the first opponent that fights back on the player's
 * own terms: it runs active sonar, so going dark does not hide you from it; it
 * shoots torpedoes, so it threatens at range; and it breaks off and repairs
 * when it is losing, so a half-finished fight comes back.
 */
function hunter(a: Actors, e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx) {
  let canSee = perceive(e, dt, dist, ctx, HUNTER.sightRange, HUNTER.darkRange, 90);

  // Its own sonar: sweeps on a timer and finds the sub regardless of the lamp.
  e.timer2 -= dt;
  if (e.timer2 <= 0) {
    e.timer2 = HUNTER.pingInterval * (0.8 + Math.random() * 0.5);
    if (dist < HUNTER.pingRange && ctx.playerAlive) {
      e.alert = 1;
      e.lastSeenX = ctx.px;
      e.lastSeenY = ctx.py;
      canSee = true;
      ctx.onEnemyShot(e.x, e.y, 'ping');
    }
  }
  e.charge = damp(e.charge, e.state === 'attack' ? 1 : 0, 2, dt);

  const hurt = e.hp / e.maxHp;
  if (e.state !== 'flee' && hurt < HUNTER.breakOff) setState(e, 'flee');

  switch (e.state) {
    case 'flee': {
      // Runs for the dark and patches itself up. Comes back meaner.
      e.hp = Math.min(e.maxHp, e.hp + HUNTER.healRate * dt);
      const ang = Math.atan2(dy, dx);
      seek(e, e.x - Math.cos(ang) * 60, e.y - Math.sin(ang) * 60, HUNTER.accel, HUNTER.speed * 1.15, dt);
      if (e.hp / e.maxHp > 0.62) setState(e, 'investigate');
      break;
    }
    case 'attack': {
      if (!canSee && e.alert < 0.25) { setState(e, 'search'); break; }
      // Holds its standoff distance and slides along the player's flank.
      const ang = Math.atan2(dy, dx);
      const side = Math.sin(e.stateTime * 0.6 + e.phase) * 26;
      seek(
        e,
        ctx.px - Math.cos(ang) * HUNTER.standoff,
        ctx.py - Math.sin(ang) * HUNTER.standoff + side,
        HUNTER.accel, HUNTER.speed, dt,
      );

      e.cooldown -= dt;
      if (e.cooldown <= 0 && dist < HUNTER.fireRange && canSee) {
        if (e.timer <= 0) e.timer = HUNTER.salvo;
        e.timer--;
        e.cooldown = e.timer > 0 ? HUNTER.salvoGap : HUNTER.reload * (0.85 + Math.random() * 0.4);
        const a2 = Math.atan2(ctx.py - e.y, ctx.px - e.x) + (Math.random() - 0.5) * 0.18;
        a.fireTorpedo(
          e.x + Math.cos(a2) * 4, e.y + Math.sin(a2) * 4, a2, 'enemy', ctx.playerTrack,
          {
            damage: HUNTER.torpedoDamage,
            blast: HUNTER.torpedoBlast,
            speed: HUNTER.torpedoSpeed,
            turnRate: HUNTER.torpedoTurn,
            life: HUNTER.torpedoLife,
            carve: 0,
           lane: e.lane, baseZ: laneZ(e.lane),
      },
        );
        ctx.onEnemyShot(e.x, e.y, 'torpedo');
        if (e.timer < 0) e.timer = 0;
      }
      break;
    }
    case 'search':
      seek(
        e,
        e.lastSeenX + Math.sin(e.stateTime * 0.9) * 40,
        e.lastSeenY + Math.cos(e.stateTime * 0.7) * 16,
        HUNTER.accel, HUNTER.speed * 0.6, dt,
      );
      if (canSee) setState(e, 'attack');
      else if (e.stateTime > 9) setState(e, 'idle');
      break;
    case 'investigate':
      seek(e, e.lastSeenX, e.lastSeenY, HUNTER.accel, HUNTER.speed * 0.9, dt);
      if (canSee && dist < HUNTER.fireRange) setState(e, 'attack');
      else if (e.stateTime > 7) setState(e, 'search');
      break;
    default:
      if (e.alert > 0.3) { setState(e, 'investigate'); break; }
      seek(
        e,
        e.homeX + Math.sin(e.stateTime * 0.22 + e.phase) * 46,
        e.homeY + Math.sin(e.stateTime * 0.17) * 10,
        HUNTER.accel * 0.7, HUNTER.speed * 0.35, dt,
      );
      break;
  }
  avoid(e, dt, 0.7, 54);
}

// ---------------------------------------------------------------------------
// capital-ship boss
// ---------------------------------------------------------------------------

/** Offsets of the two weapon pods in the boss's local frame. */
const POD_A = { x: 9, y: 6.5, r: 6.5 };   // dorsal forward — the gun battery
const POD_B = { x: -11, y: -5.5, r: 6.5 }; // ventral aft — the torpedo room

function podWorld(e: Enemy, p: { x: number; y: number }) {
  return { x: e.x + p.x * e.facing, y: e.y + p.y };
}

/**
 * Route damage into whichever pod was hit. The hull itself is heavily armoured
 * until both pods are gone, so the fight has a shape: silence its guns, silence
 * its tubes, then finish it.
 */
export function bossHit(e: Enemy, dealt: number, hx: number, hy: number): number {
  const a = podWorld(e, POD_A);
  const b = podWorld(e, POD_B);
  if (e.podA > 0 && hyp(hx - a.x, hy - a.y) < POD_A.r) {
    e.podA = Math.max(0, e.podA - dealt);
    return dealt * 0.3;
  }
  if (e.podB > 0 && hyp(hx - b.x, hy - b.y) < POD_B.r) {
    e.podB = Math.max(0, e.podB - dealt);
    return dealt * 0.3;
  }
  // Bare hull. Wide open once the pods are gone, a slog before that.
  return e.podA > 0 || e.podB > 0 ? dealt * 0.35 : dealt * 1.25;
}

function dreadnought(a: Actors, e: Enemy, dt: number, dx: number, dy: number, dist: number, ctx: ActorCtx) {
  // Sleeps until the sub is properly inside its water, or until it is shot.
  if (e.alert < 0.05 && dist > 170) {
    e.charge = 0;
    return;
  }
  e.alert = Math.min(1, e.alert + dt * 0.8);
  e.charge = damp(e.charge, 1, 0.8, dt);
  e.lastSeenX = ctx.px;
  e.lastSeenY = ctx.py;

  const gunsUp = e.podA > 0;
  const tubesUp = e.podB > 0;
  const enraged = !gunsUp && !tubesUp;

  if (enraged && e.state !== 'rush' && e.state !== 'recover') {
    setState(e, 'rush');
    e.timer = 0;
  }

  if (e.state === 'rush') {
    // Stripped of weapons it simply charges, which is both the most dangerous
    // and the most readable phase — a wall of steel with a long wind-up.
    e.timer += dt;
    if (e.timer < 1.6) {
      // Wind-up: backs off and lines up.
      const ang = Math.atan2(dy, dx);
      seek(e, e.x - Math.cos(ang) * 30, e.y - Math.sin(ang) * 30, DREAD.accel, DREAD.speed, dt, 1.2);
      e.facing = dx >= 0 ? 1 : -1;
    } else {
      seek(e, ctx.px, ctx.py, DREAD.accel * 5, DREAD.ramSpeed, dt, 0.5);
      if (dist < e.radius + ctx.playerRadius + 2) {
        ctx.hitPlayer(DREAD.ramDamage);
        ctx.onShake(0.8);
        setState(e, 'recover');
        e.timer = 0;
      }
      if (e.timer > 5) { setState(e, 'recover'); e.timer = 0; }
    }
    return;
  }
  if (e.state === 'recover') {
    e.timer += dt;
    seek(e, e.homeX, e.homeY, DREAD.accel, DREAD.speed * 0.6, dt, 1.6);
    if (e.timer > 2.6) { setState(e, 'rush'); e.timer = 0; }
    return;
  }

  // Standing engagement: it patrols its arena, keeping the player broadside.
  setState(e, 'attack');
  const ang = Math.atan2(dy, dx);
  seek(
    e,
    ctx.px - Math.cos(ang) * 74,
    clamp(ctx.py - Math.sin(ang) * 40, e.homeY - 46, e.homeY + 46),
    DREAD.accel, DREAD.speed, dt, 1.4,
  );
  e.facing = dx >= 0 ? 1 : -1;

  // --- gun battery -----------------------------------------------------
  if (gunsUp) {
    e.cooldown -= dt;
    if (e.cooldown <= 0 && dist < DREAD.gunRange) {
      if (e.timer <= 0) e.timer = DREAD.gunBurst;
      e.timer--;
      e.cooldown = e.timer > 0 ? DREAD.gunInterval : DREAD.gunReload;
      const p = podWorld(e, POD_A);
      const a2 = leadAngle(e, ctx, DREAD.bulletSpeed, 0.6, 0.1);
      a.fireBullet(p.x, p.y, a2, 'enemy', DREAD.bulletSpeed, DREAD.bulletDamage, e.lane, laneZ(e.lane));
      ctx.onEnemyShot(p.x, p.y, 'gun');
      if (e.timer < 0) e.timer = 0;
    }
  }

  // --- torpedo room ----------------------------------------------------
  if (tubesUp) {
    e.timer2 -= dt;
    if (e.timer2 <= 0) {
      e.timer2 = DREAD.torpedoInterval * (0.8 + Math.random() * 0.4);
      const p = podWorld(e, POD_B);
      for (let i = -1; i <= 1; i++) {
        const a2 = Math.atan2(ctx.py - p.y, ctx.px - p.x) + i * 0.22;
        a.fireTorpedo(p.x, p.y, a2, 'enemy', ctx.playerTrack, {
          damage: DREAD.torpedoDamage,
          blast: 10,
          speed: 34,
          turnRate: 1.1,
          life: 7,
          carve: 0,
         lane: e.lane, baseZ: laneZ(e.lane),
      });
      }
      ctx.onEnemyShot(p.x, p.y, 'torpedo');
    }
  }
  void ctx.playerAlive;
}
